import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import {
  fetchAccountantMembershipPayments,
  verifyAccountantMembershipPayment,
  verifyAccountantMembershipPaymentsBulk,
} from "../api/membershipPaymentsApi";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../global/directory";
import { getCsrfToken } from "../../../public/api/authApi";
import { GlobalModal } from "../../../global/GlobalModal";
import GlobalErrorFlashModal from "../../../global/GlobalErrorFlashModal";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import GlobalBadge from "../../../global/GlobalBadge";
import PaymentProofViewButton from "../../payment/PaymentProofViewButton";
import "../styles/VolunteerTable.css";
import "../styles/participantsList.css";
import "../../tournaments/styles/tournamentAdminApprovals.css";
import "../styles/tournamentPaymentsPage.css";
import { formatMembershipBulkPaymentSkipSuffix } from "../utils/membershipBulkSkipMessage";
import { useMembershipRole } from "../utils/useMembershipRole";
import { MEMBERSHIPS_PAYMENTS_SUBMODULE_SLUG } from "../utils/membershipPermissionSlugs";
import { GlobalTableActionButton } from "../../../global/GlobalTableActionButton";

const PAGE_SIZE = 50;
const COL_KEY = "girinestham.accountant.membershipPayments.columns.v1";

const COLUMN_DEFS = [
  { id: "student_code", label: "Student code", defaultVisible: true, sortable: true },
  { id: "student_name", label: "Student name", defaultVisible: true, sortable: true },
  { id: "student_contact", label: "Contact", defaultVisible: true, sortable: true },
  { id: "membership_name", label: "Membership", defaultVisible: true, sortable: true },
  { id: "application_code", label: "Application code", defaultVisible: true, sortable: true },
  { id: "start_date", label: "Start date", defaultVisible: true, sortable: false },
  { id: "end_date", label: "End date", defaultVisible: true, sortable: false },
  { id: "application_verified", label: "Application verified", defaultVisible: true, sortable: true },
  { id: "payment_verified", label: "Payment verified", defaultVisible: true, sortable: true },
  { id: "receipt_no", label: "Receipt no.", defaultVisible: true, sortable: true },
  { id: "payment_date", label: "Payment date", defaultVisible: true, sortable: true },
  { id: "payment_time", label: "Payment time", defaultVisible: false, sortable: false },
  { id: "amount_paid", label: "Amount", defaultVisible: true, sortable: true },
  { id: "payment_mode_name", label: "Mode", defaultVisible: true, sortable: true },
  { id: "payment_sub_type_name", label: "Payment sub-type", defaultVisible: true, sortable: true },
  { id: "purpose_name", label: "Purpose", defaultVisible: true, sortable: true },
  { id: "transaction_id", label: "Transaction ID", defaultVisible: true, sortable: true },
  { id: "payment_proof", label: "Proof", defaultVisible: true, sortable: false },
  { id: "collected_by_name", label: "Collected by", defaultVisible: true, sortable: true },
  { id: "payment_remarks", label: "Remarks", defaultVisible: false, sortable: false },
];

function loadVis() {
  let saved = null;
  try {
    const raw = localStorage.getItem(COL_KEY);
    if (raw) saved = JSON.parse(raw);
  } catch {
    /* ignore */
  }
  const initial = {};
  for (const c of COLUMN_DEFS) initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  return initial;
}

function formatDateCell(value) {
  if (value == null || value === "") return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) d = new Date(raw.replace(" ", "T"));
    if (!isValid(d)) d = new Date(raw);
    return isValid(d) ? format(d, "PP") : "—";
  } catch {
    return "—";
  }
}

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `₹${n.toFixed(2)}` : "—";
}

function isTruthyBool(v) {
  return v === true || v === 1 || v === "1";
}

function cellText(row, colId) {
  switch (colId) {
    case "start_date":
    case "end_date":
    case "payment_date":
      return formatDateCell(row[colId]);
    case "amount_paid":
      return formatMoney(row[colId]);
    case "payment_verified":
    case "application_verified":
      return isTruthyBool(row[colId]) ? "Yes" : "No";
    case "payment_proof":
      return row.payment_proof ? String(row.payment_proof) : "—";
    default:
      return row[colId] ?? "—";
  }
}

export default function MembershipPayments() {
  const perms = useMembershipRole(MEMBERSHIPS_PAYMENTS_SUBMODULE_SLUG);
  const [rows, setRows] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 });
  const [sortColumn, setSortColumn] = useState("payment_date");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [colVis, setColVis] = useState(() => loadVis());
  const headerCheckboxRef = useRef(null);
  const [detail, setDetail] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState(() => new Set());

  const initialFilters = {
    student_search: "",
    column: "",
    value: "",
  };
  const [draft, setDraft] = useState(() => ({ ...initialFilters }));
  const [applied, setApplied] = useState(() => ({ ...initialFilters }));

  useEffect(() => {
    const t = setTimeout(() => setApplied({ ...draft }), 400);
    return () => clearTimeout(t);
  }, [draft]);

  useEffect(() => {
    setPage(1);
    setSelectedPaymentIds(new Set());
  }, [applied.student_search, applied.column, applied.value]);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const queryFilters = {
        student_search: (applied.student_search || "").trim() || undefined,
        page: String(page),
        limit: String(PAGE_SIZE),
        sort_column: sortColumn,
        sort_order: sortOrder,
      };
      if (applied.column && applied.value) {
        if (applied.column === "membership_name") {
          const match = (memberships || []).find(
            (m) => String(m.membership_name) === String(applied.value),
          );
          if (match?.id) queryFilters.membership_id = String(match.id);
        } else if (applied.column === "payment_verified") {
          queryFilters.payment_verified = applied.value === "Verified" ? "1" : "0";
        } else if (applied.column === "application_verified") {
          queryFilters.application_verified = applied.value === "Verified" ? "1" : "0";
        } else if (applied.column === "student_name" || applied.column === "student_code") {
          queryFilters.student_search = String(applied.value || "").trim() || undefined;
        }
      }
      const res = await fetchAccountantMembershipPayments({
        ...queryFilters,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMemberships(res?.meta?.memberships || []);
      const m = res?.meta || {};
      setMeta({
        total: Number(m.total) || 0,
        page: Number(m.page) || page,
        limit: Number(m.limit) || PAGE_SIZE,
        totalPages: Math.max(Number(m.totalPages) || 1, 1),
      });
    } catch (e) {
      setErrorMsg(e?.message || "Could not load data.");
      setRows([]);
      setMemberships([]);
      setMeta({ total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [applied, page, sortColumn, sortOrder]);

  useEffect(() => {
    load();
  }, [load]);

  const membershipOpts = useMemo(
    () => (memberships || []).map((x) => ({ value: String(x.id), label: x.membership_name })),
    [memberships],
  );
  const valueFilterOptions = useMemo(() => {
    if (!draft.column) return [];
    if (draft.column === "payment_verified" || draft.column === "application_verified") {
      return [
        { value: "Verified", label: "Verified" },
        { value: "Not verified", label: "Not verified" },
      ];
    }
    if (draft.column === "membership_name") {
      return membershipOpts.map((m) => ({ value: m.label, label: m.label }));
    }
    const seen = new Set();
    const out = [];
    for (const row of rows) {
      const raw = row[draft.column];
      if (raw == null || raw === "") continue;
      const label = String(raw);
      if (seen.has(label)) continue;
      seen.add(label);
      out.push({ value: label, label });
    }
    return out.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [draft.column, membershipOpts, rows]);

  const filteredRows = useMemo(() => {
    const col = applied.column;
    const val = String(applied.value || "").trim();
    if (!col || !val) return rows;
    const match = val.toLowerCase();
    return rows.filter((row) => {
      if (col === "payment_verified" || col === "application_verified") {
        const flag = isTruthyBool(row[col]) ? "verified" : "not verified";
        return flag === match;
      }
      const raw = cellText(row, col);
      return String(raw ?? "").trim().toLowerCase() === match;
    });
  }, [rows, applied.column, applied.value]);

  const visibleCols = useMemo(() => COLUMN_DEFS.filter((c) => colVis[c.id]), [colVis]);

  const selectablePaymentIdsOnPage = useMemo(
    () => filteredRows.filter((r) => r.payment_id != null).map((r) => r.payment_id),
    [filteredRows],
  );
  const allPagePaymentsSelected =
    selectablePaymentIdsOnPage.length > 0 &&
    selectablePaymentIdsOnPage.every((id) => selectedPaymentIds.has(id));
  const somePagePaymentsSelected = selectablePaymentIdsOnPage.some((id) =>
    selectedPaymentIds.has(id),
  );

  useEffect(() => {
    const el = headerCheckboxRef.current;
    if (el) el.indeterminate = !allPagePaymentsSelected && somePagePaymentsSelected;
  }, [allPagePaymentsSelected, somePagePaymentsSelected]);

  const toggleSort = (c) => {
    if (!c.sortable) return;
    setPage(1);
    if (sortColumn === c.id) setSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
    else {
      setSortColumn(c.id);
      setSortOrder("ASC");
    }
  };

  const toggleAllPaymentsOnPage = () => {
    setSelectedPaymentIds((prev) => {
      const next = new Set(prev);
      const allOn =
        selectablePaymentIdsOnPage.length > 0 &&
        selectablePaymentIdsOnPage.every((id) => next.has(id));
      if (allOn) selectablePaymentIdsOnPage.forEach((id) => next.delete(id));
      else selectablePaymentIdsOnPage.forEach((id) => next.add(id));
      return next;
    });
  };

  const csrf = async () => {
    let t = localStorage.getItem("csrf-token");
    if (!t) {
      t = await getCsrfToken();
      localStorage.setItem("csrf-token", t);
    }
    return t;
  };

  const handlePaymentVerification = async (row, wantVerified) => {
    const pid = row?.payment_id;
    if (pid == null) return;
    setVerifyBusy(pid);
    setErrorMsg("");
    try {
      const res = await verifyAccountantMembershipPayment(pid, await csrf(), wantVerified);
      setSuccessMsg(res?.message || (wantVerified ? "Payment marked verified." : "Payment marked not verified."));
      setSelectedPaymentIds((prev) => {
        const next = new Set(prev);
        next.delete(pid);
        return next;
      });
      await load();
      setDetail((d) => (d && d.payment_id === pid ? { ...d, payment_verified: wantVerified } : d));
    } catch (e) {
      setErrorMsg(e?.message || "Could not update payment verification.");
    } finally {
      setVerifyBusy(null);
    }
  };

  const handleBulkPaymentVerification = async (wantVerified) => {
    const ids = Array.from(selectedPaymentIds).filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length === 0) {
      setErrorMsg("Select at least one payment row.");
      return;
    }
    setBulkBusy(true);
    setErrorMsg("");
    try {
      const res = await verifyAccountantMembershipPaymentsBulk(ids, await csrf(), wantVerified);
      const processed = Number(res?.data?.processed ?? 0);
      const skipped = Number(res?.data?.skipped ?? 0);
      const skipDetail = formatMembershipBulkPaymentSkipSuffix(res?.data?.skip_reasons);
      if (processed === 0) {
        setSuccessMsg("");
        setErrorMsg(
          (res?.message && String(res.message).trim()) ||
            `No payments were updated.${skipDetail}`,
        );
      } else {
        setErrorMsg("");
        setSuccessMsg(
          res?.message?.trim() ||
            (wantVerified
              ? `Verified ${processed} payment(s)${skipped ? ` · ${skipped} skipped.` : "."}${skipDetail}`
              : `Marked ${processed} payment(s) not verified${skipped ? ` · ${skipped} skipped.` : "."}${skipDetail}`),
        );
      }
      setSelectedPaymentIds(new Set());
      await load();
    } catch (e) {
      setErrorMsg(e?.message || "Bulk update failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  const tableHeaders = useMemo(
    () => [
      {
        key: "_select",
        className: "checkbox-cell",
        label: (
          <input
            ref={headerCheckboxRef}
            type="checkbox"
            aria-label="Select all payments on this page"
            checked={allPagePaymentsSelected}
            onChange={toggleAllPaymentsOnPage}
            disabled={filteredRows.length === 0 || selectablePaymentIdsOnPage.length === 0}
          />
        ),
      },
      ...visibleCols.map((c) =>
        c.sortable
          ? {
              key: c.id,
              label: (
                <button type="button" className="vpart-sort-btn" onClick={() => toggleSort(c)}>
                  {c.label}
                  {sortColumn === c.id ? (
                    <span className="vpart-sort-ind" aria-hidden>
                      {sortOrder === "ASC" ? "▲" : "▼"}
                    </span>
                  ) : null}
                </button>
              ),
            }
          : { key: c.id, label: c.label },
      ),
      { key: "_actions", className: "td-approvals-actions-col", label: "Actions" },
    ],
    [
      visibleCols,
      sortColumn,
      sortOrder,
      allPagePaymentsSelected,
      filteredRows.length,
      selectablePaymentIdsOnPage.length,
    ],
  );

  const getExportData = useCallback(() => {
    const headers = visibleCols.map((c) => c.label);
    const exportRows = filteredRows.map((row) =>
      visibleCols.map((c) => String(cellText(row, c.id) ?? "")),
    );
    return { headers, rows: exportRows };
  }, [filteredRows, visibleCols]);

  return (
    <div className="vpart-page accountant-tpay-page">
      <GlobalErrorFlashModal message={errorMsg} onDismiss={() => setErrorMsg("")} />
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />

      <header className="vpart-hero">
        <p className="vpart-hero__eyebrow">Accountant</p>
        <h1 className="vpart-hero__title">Membership payments</h1>
        <p className="vpart-hero__sub">
          Verify membership application payments. Only payments with purpose containing
          "membership" are shown.
        </p>
      </header>

      <GlobalDirectoryShell
        className="gds-shell--compact"
        filters={
          <GlobalDirectoryFiltersSection
            className="ub-filters ub-user-directory-filters"
            searchLabel="Search student"
            searchPlaceholder="Name, code, phone"
            searchValue={draft.student_search}
            onSearchChange={(v) => setDraft((f) => ({ ...f, student_search: v }))}
            columnLabel="Column"
            columnValue={draft.column}
            onColumnChange={(v) => setDraft((f) => ({ ...f, column: v, value: "" }))}
            gridColumnDefs={COLUMN_DEFS}
            columnPlaceholderOption="Select"
            valueLabel="Value"
            valueValue={draft.value}
            onValueChange={(v) => setDraft((f) => ({ ...f, value: v }))}
            valueOptions={valueFilterOptions}
            valuePlaceholderOption="Any"
            onClear={() => setDraft((f) => ({ ...f, column: "", value: "" }))}
            clearFilterLabel="Clear filter"
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={COLUMN_DEFS}
                visibility={colVis}
                onVisibilityChange={setColVis}
                storageKey={COL_KEY}
                panelId="mpay-cols-panel"
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu
                getExportData={getExportData}
                filenameBase="membership-payments"
                buttonLabel="Export as"
              />
            }
          />
        }
        approvals={
          <div className="bulk-actions-bar">
            <div className="bulk-actions-info">
              <span id="mpay-bulk-title" className="selection-count">
                Selection
              </span>
              <span className="selection-hint">
                Payments can be marked verified only when application/renewal is verified.
              </span>
            </div>
            <div className="bulk-actions-buttons">
              <span className="selection-count" aria-live="polite">
                {selectedPaymentIds.size} selected
              </span>
              {perms.canEdit ? (
                <>
              <GlobalButton
                type="button"
                variant="primary"
                size="sm"
                disabled={selectedPaymentIds.size === 0 || bulkBusy}
                onClick={() => handleBulkPaymentVerification(true)}
              >
                {bulkBusy ? "Working…" : "Mark selected verified"}
              </GlobalButton>
              <GlobalButton
                type="button"
                variant="ghost"
                size="sm"
                disabled={selectedPaymentIds.size === 0 || bulkBusy}
                onClick={() => handleBulkPaymentVerification(false)}
              >
                {bulkBusy ? "Working…" : "Mark selected not verified"}
              </GlobalButton>
                </>
              ) : null}
            </div>
          </div>
        }
        grid={
          <GlobalDirectoryGridSection
            loading={loading}
            loadingMessage="Loading…"
            headers={tableHeaders}
            isEmpty={!loading && filteredRows.length === 0}
            emptyMessage="No rows match your filters."
            tableAriaLabel="Membership payments"
            wrapClassName="vt-table-wrap td-approvals-table-scroll"
            tableClassName="vt-table"
          >
            {!loading &&
              filteredRows.map((row) => (
                <tr key={row.entry_key || `${row.application_id}-${row.payment_id}`}>
                  <td className="checkbox-cell" style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={row.payment_id != null && selectedPaymentIds.has(row.payment_id)}
                      disabled={row.payment_id == null}
                      onChange={() => {
                        if (row.payment_id == null) return;
                        setSelectedPaymentIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(row.payment_id)) next.delete(row.payment_id);
                          else next.add(row.payment_id);
                          return next;
                        });
                      }}
                      aria-label={`Select payment for ${row.student_name ?? "student"}`}
                    />
                  </td>
                  {visibleCols.map((c) => (
                    <td key={c.id}>
                      {c.id === "payment_verified" ? (
                        <GlobalBadge
                          active={isTruthyBool(row.payment_verified)}
                          activeText="Verified"
                          inactiveText="Not verified"
                        />
                      ) : c.id === "application_verified" ? (
                        <GlobalBadge
                          active={isTruthyBool(row.application_verified)}
                          activeText="Verified"
                          inactiveText="Not verified"
                        />
                      ) : c.id === "membership_name" ? (
                        <span className="tpay-chip" title={String(cellText(row, c.id))}>
                          {cellText(row, c.id)}
                        </span>
                      ) : c.id === "student_name" ? (
                        <span style={{ fontWeight: 600 }}>{cellText(row, c.id)}</span>
                      ) : c.id === "payment_proof" ? (
                        <PaymentProofViewButton proofUrl={row.payment_proof} />
                      ) : (
                        cellText(row, c.id)
                      )}
                    </td>
                  ))}
                  <td className="td-approvals-actions">
                    <div className="td-approvals-actions-inner tpay-actions">
                      <GlobalTableActionButton
                        action="view"
                        onClick={() => setDetail(row)}
                      />
                      {perms.canEdit && row.payment_id != null ? (
                        <>
                          <GlobalTableActionButton
                            action="verify"
                            loading={verifyBusy === row.payment_id}
                            onClick={() => handlePaymentVerification(row, true)}
                          />
                          <GlobalTableActionButton
                            action="unverify"
                            variant="ghost"
                            disabled={verifyBusy === row.payment_id}
                            onClick={() => handlePaymentVerification(row, false)}
                          />
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
          </GlobalDirectoryGridSection>
        }
        pagination={
          loading ? null : (
            <GlobalDirectoryPaginationSection
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              pageSize={meta.limit}
              onPageChange={setPage}
              label="Membership payments pages"
            />
          )
        }
      />

      <GlobalModal
        open={detail != null}
        onClose={() => setDetail(null)}
        title="Payment detail"
        description={detail ? `${detail.student_name} — ${detail.membership_name}` : undefined}
        size="lg"
        classNames={{
          modal: "gff-modal vpart-reg-modal",
          header: "gff-modal__header vpart-reg-modal__header",
          title: "gff-modal__title vpart-reg-modal__title",
          description: "gff-modal__description vpart-reg-modal__subtitle",
          body: "gff-modal__body vpart-reg-modal__body",
        }}
      >
        {detail ? (
          <div className="vpart-reg-modal__scroll">
            <article className="vpart-reg-card">
              <h3 className="vpart-reg-card__title">Student</h3>
              <div className="vpart-reg-fields">
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Code</span>
                  <span className="vpart-reg-field__value">{detail.student_code ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Name</span>
                  <span className="vpart-reg-field__value">{detail.student_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Contact</span>
                  <span className="vpart-reg-field__value">{detail.student_contact ?? "—"}</span>
                </div>
              </div>
            </article>
            <article className="vpart-reg-card">
              <h3 className="vpart-reg-card__title">Membership Application</h3>
              <div className="vpart-reg-fields">
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Application code</span>
                  <span className="vpart-reg-field__value">{detail.application_code ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Membership</span>
                  <span className="vpart-reg-field__value">{detail.membership_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Application verified</span>
                  <span className="vpart-reg-field__value">
                    {isTruthyBool(detail.application_verified) ? "Yes" : "No"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Type</span>
                  <span className="vpart-reg-field__value">
                    {detail.entry_type === "renewal" ? "Renewal" : "Application"}
                  </span>
                </div>
              </div>
            </article>
            <article className="vpart-reg-card vpart-reg-card--payment">
              <h3 className="vpart-reg-card__title">Payment</h3>
              <div className="vpart-reg-fields">
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Receipt</span>
                  <span className="vpart-reg-field__value">{detail.receipt_no ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Amount</span>
                  <span className="vpart-reg-field__value">{formatMoney(detail.amount_paid)}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Date / time</span>
                  <span className="vpart-reg-field__value">
                    {formatDateCell(detail.payment_date)}
                    {detail.payment_time ? ` · ${detail.payment_time}` : ""}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Payment verified</span>
                  <span className="vpart-reg-field__value">
                    {isTruthyBool(detail.payment_verified) ? "Yes" : "No"}
                  </span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Mode</span>
                  <span className="vpart-reg-field__value">{detail.payment_mode_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Sub-type</span>
                  <span className="vpart-reg-field__value">{detail.payment_sub_type_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Purpose</span>
                  <span className="vpart-reg-field__value">{detail.purpose_name ?? "—"}</span>
                </div>
                <div className="vpart-reg-field">
                  <span className="vpart-reg-field__label">Proof</span>
                  <span className="vpart-reg-field__value">
                    <PaymentProofViewButton proofUrl={detail.payment_proof} />
                  </span>
                </div>
              </div>
            </article>
            {detail.payment_id != null ? (
              <div className="tpay-modal-actions">
                <p className="tpay-modal-hint">
                  Payment can be marked verified only if application/renewal is verified.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", justifyContent: "flex-end" }}>
                  {perms.canEdit ? (
                    <>
                  <GlobalButton
                    type="button"
                    variant="primary"
                    disabled={verifyBusy === detail.payment_id}
                    onClick={() => handlePaymentVerification(detail, true)}
                  >
                    {verifyBusy === detail.payment_id ? "Working…" : "Mark verified"}
                  </GlobalButton>
                  <GlobalButton
                    type="button"
                    variant="ghost"
                    disabled={verifyBusy === detail.payment_id}
                    onClick={() => handlePaymentVerification(detail, false)}
                  >
                    Mark not verified
                  </GlobalButton>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </GlobalModal>
    </div>
  );
}
